import React from 'react';
import { Form, Button, Spinner, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { User, Phone, Mail, Lock, Eye, EyeOff, ArrowRight, Clapperboard, Users } from 'lucide-react';
import { triggerMediumHaptic } from '../../utils/haptics';

const ROLES = {
    TALENT: { name: 'Talent', desc: '', code: '300' },
    PRODUCER: { name: 'Director/Producer', desc: '', code: '200' },
    CASTING: { name: 'Casting Director', desc: '', code: '400' },
};

const RegisterStepForm = ({
  selectedRoleCode,
  setSelectedRoleCode,
  showTalentList,
  setShowTalentList,
  setSelectedTalent,
  selectedTalent,
  lookupOptions,
  fullName,
  setFullName,
  mobile,
  setMobile,
  email,
  setEmail,
  password,
  setPassword,
  setConfirmPassword,
  showPassword,
  setShowPassword,
  tnc,
  setTnc,
  openTncModal,
  errors,
  setErrors,
  handleInitiateRegistration,
  loading
}) => {
  return (
    <Form onSubmit={handleInitiateRegistration}>
      <label className="form-label-custom mb-2">I am a</label>
     
      <div className="role-selection-wrapper mb-4">
        {Object.values(ROLES).map((role) => {
          let Icon = User;
          if (role.code === ROLES.PRODUCER.code) Icon = Clapperboard;
          if (role.code === ROLES.CASTING.code) Icon = Users;
          
          return (
            <div 
              key={role.code}
              className={`role-card-item ${selectedRoleCode === role.code ? 'active' : ''}`}
              onClick={() => {
                setSelectedRoleCode(role.code);
                if (role.code === ROLES.TALENT.code) {
                  setShowTalentList(true);
                } else {
                  setShowTalentList(false);
                  setSelectedTalent(null);
                  setErrors(prev => ({...prev, talent: null})); 
                }
              }}
            >
              <Icon size={16} className={selectedRoleCode === role.code ? "text-red" : "text-secondary"} />
              <span>{role.name}</span>
            </div>
          );
        })}
      </div>

      {showTalentList && (
        <Form.Group className="mb-2 text-start">
          <Form.Label className="form-label-custom">Select Talent <span className="text-danger">*</span></Form.Label>
          <div className={errors.talent ? 'select-error-wrapper' : ''}>
            <Select
              options={lookupOptions.talent}
              value={selectedTalent}
              onChange={(selected) => {
                setSelectedTalent(selected);
                if(selected) setErrors(prev => ({ ...prev, talent: null }));
              }}
              placeholder="Select primary talent..."
              className="custom-select"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: '#2a2d30 !important', 
                  borderColor: '#444',
                  color: 'white'
                }),
                menu: (base) => ({ ...base, backgroundColor: '#2a2d30' }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? '#3e4247' : '#2a2d30',
                  color: 'white'
                }),
                singleValue: (base) => ({ ...base, color: 'white' }),
              }}
            />
          </div>
          {errors.talent && <span className="error-text-custom">{errors.talent}</span>}
        </Form.Group>
      )}

      <Form.Group className="mb-3 text-start">
        <Form.Label className="form-label-custom">Full Name <span className="text-danger">*</span></Form.Label>
        <InputGroup className={`login-input-group ${errors.fullName ? 'border-danger' : ''}`}>
          <InputGroup.Text><User size={18}/></InputGroup.Text>
          <Form.Control 
            type="text" 
            placeholder="Enter your full name" 
            value={fullName} 
            onChange={(e) => {
              setFullName(e.target.value);
              if(errors.fullName) setErrors(prev => ({...prev, fullName: null}));
            }} 
            className="form-ctrl-login-custom"
          />
        </InputGroup>
        {errors.fullName && <span className="error-text-custom">{errors.fullName}</span>}
      </Form.Group>

      <Form.Group className="mb-3 text-start">
        <Form.Label className="form-label-custom">Mobile Number <span className="text-danger">*</span></Form.Label>
        <InputGroup className={`login-input-group ${errors.mobile ? 'border-danger' : ''}`}>
          <InputGroup.Text className="border-end border-secondary pe-2">+91</InputGroup.Text>
          <InputGroup.Text className="ps-2"><Phone size={18}/></InputGroup.Text>
          <Form.Control 
            type="tel" 
            placeholder="Enter your mobile number" 
            value={mobile} 
            onChange={(e) => {
              setMobile(e.target.value.replace(/\D/g, '')); 
              if(errors.mobile) setErrors(prev => ({...prev, mobile: null}));
            }} 
            className="form-ctrl-login-custom"
          />
        </InputGroup>
        {errors.mobile && <span className="error-text-custom">{errors.mobile}</span>}
      </Form.Group>

      <Form.Group className="mb-3 text-start">
        <Form.Label className="form-label-custom">Email Address <span className="text-danger">*</span></Form.Label>
        <InputGroup className={`login-input-group ${errors.email ? 'border-danger' : ''}`}>
          <InputGroup.Text><Mail size={18}/></InputGroup.Text>
          <Form.Control 
            type="email" 
            placeholder="Enter your email address" 
            value={email} 
            onChange={(e) => {
              setEmail(e.target.value);
              if(errors.email) setErrors(prev => ({...prev, email: null}));
            }} 
            className="form-ctrl-login-custom"
          />
        </InputGroup>
        {errors.email && <span className="error-text-custom">{errors.email}</span>}
      </Form.Group>

      <Form.Group className="mb-4 text-start">
        <Form.Label className="form-label-custom">Password <span className="text-danger">*</span></Form.Label>
        <InputGroup className={`login-input-group ${errors.password ? 'border-danger' : ''}`}>
          <InputGroup.Text><Lock size={18}/></InputGroup.Text>
          <Form.Control 
            type={showPassword ? "text" : "password"} 
            placeholder="Create a strong password" 
            value={password} 
            onChange={(e) => {
              const newPassword = e.target.value;
              setPassword(newPassword);
              setConfirmPassword(newPassword); 
              if(errors.password) setErrors(prev => ({...prev, password: null}));
            }} 
            className="form-ctrl-login-custom"
          />
          <InputGroup.Text 
            className="cursor-pointer" 
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
          </InputGroup.Text>
        </InputGroup>
        {errors.password && <span className="error-text-custom">{errors.password}</span>}
      </Form.Group>
      
      <Form.Group className="mb-4 text-start custom-checkbox">
        <Form.Check 
          type="checkbox" 
          id="tnc" 
          checked={tnc} 
          onChange={(e) => {
            if (e.target.checked) {
              openTncModal(e);
            } else {
              setTnc(false);
            }
          }} 
          label={
            <span className="small text-secondary ms-1">
              I agree to the <span className="text-primary text-decoration-none" style={{cursor: 'pointer'}} onClick={openTncModal}>Terms and Conditions</span> and Privacy Policy
            </span>
          } 
        />
        {errors.tnc && <span className="error-text-custom d-block mt-1">{errors.tnc}</span>}
      </Form.Group>

      <div className="sticky-mobile-cta">
        <Button 
          type="submit" 
          className="btn-auth-primary w-100 native-btn" 
          disabled={loading}
          onClick={() => triggerMediumHaptic()}
        >
          {loading ? (
            <>
              <Spinner size="sm" animation="border" />
              <span>REGISTERING...</span>
            </>
          ) : (
            <>
              <span>REGISTER & GET OTP</span>
              <ArrowRight size={18} />
            </>
          )}
        </Button>
      </div>

      <p className="auth-footer-link text-secondary text-center mt-4 mb-0 small">
        Already have an account? <Link to="/login" className="text-red text-decoration-none fw-bold">Sign in</Link>
      </p>
    </Form>
  );
};

export default RegisterStepForm;
